import {
  brandName,
  ORPHAN_REVIEW_COMPANY_ID,
  removeUndefinedProps,
  useConfig,
  useValidation,
} from '@seek/libs-shared';
import translations from './.vocab';

import { Box, Loader } from 'braid-design-system';
import { useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';
import type { SearchCompanyProfileResult } from '../../shared/services/companyProfileService/types';
import type { LocationSuggestion } from '../../shared/services/locationService/locationService';
import type {
  ConfirmedRoles,
  RoleTitle,
} from '../../shared/services/roleService/roleService';
import {
  trackClickCompanyInterceptReviewDifferentCompany,
  trackClickCompanyInterceptSelection,
  trackWriteAReviewStarted,
  type ReviewFormContext,
} from '../../shared/tracking/writeAReview/trackingEvents';
import { ExperimentTwoProgress, Progress, type ReviewPayload } from '../type';
import { validationRules } from '../validation/validationRules';
import { CompaniesListPage } from './CompaniesListPage';
import { OverallRatingPage } from './OverallRatingPage';
import { SubmitSuccessPage } from './SubmitSuccessPage';
import { YourExperiencePage } from './YourExperiencePage';
import { YourFeedbackPage } from './YourFeedbackPage';
import { YourRolePage } from './YourRolePage';
import { YourSalaryPage } from './YourSalaryPage';

import { useTranslations } from '@vocab/react';
import { MetaTag } from '../../companyProfiles/components/MetaTag/MetaTag';
import { NoIndexTag } from '../../companyProfiles/components/NoIndexTag/NoIndexTag';
import { CareerDevelopmentPage } from './ReviewsExperimentTwoPages/CareerDevelopmentPage';
import { DiversityEqualOpptyPage } from './ReviewsExperimentTwoPages/DiversityEqualOpptyPage';
import { ManagementPage } from './ReviewsExperimentTwoPages/ManagementPage';
import { PerksAndBenefitsPage } from './ReviewsExperimentTwoPages/PerksAndBenefitsPage';
import { ReviewCompanyPage } from './ReviewsExperimentTwoPages/ReviewCompanyPage';
import { SalaryPage } from './ReviewsExperimentTwoPages/SalaryPage';
import { SubmitSuccessPage as ExpTwoSubmitSuccessPage } from './ReviewsExperimentTwoPages/SubmitSuccessPage';
import { WorkingEnvironmentPage } from './ReviewsExperimentTwoPages/WorkingEnvironmentPage';
import { WorkLifeBalancePage } from './ReviewsExperimentTwoPages/WorkLifeBalancePage';
interface Props {
  getConfirmedRoles: () => Promise<ConfirmedRoles[]>;
  getRoleTitles: (query: string) => Promise<RoleTitle[]>;
  reviewFormContext: ReviewFormContext;
  searchCompanyProfiles: (
    query: string,
    limit?: number,
  ) => Promise<SearchCompanyProfileResult>;
  searchLocations: (query: string) => Promise<LocationSuggestion[]>;
  submitReview: (review: ReviewPayload) => Promise<boolean>;
  submitReviewExperimentTwo: (
    review: ReviewPayload,
    page: ExperimentTwoProgress,
  ) => Promise<boolean>;
  updateRole: (role: ConfirmedRoles) => Promise<boolean>;
  variantValueForTracking: string | null;
  companyNameQueryParam?: string;
  experimentTwoReviewId?: string;
  isExperimentTwo?: boolean;
}

const SUGGESTION_LIMIT = 7;

export const WriteAReviewPage = ({
  getRoleTitles,
  searchCompanyProfiles,
  searchLocations,
  companyNameQueryParam,
  reviewFormContext,
  getConfirmedRoles,
  updateRole,
  submitReview,
  submitReviewExperimentTwo,
  experimentTwoReviewId,
  isExperimentTwo,
  variantValueForTracking,
}: Props) => {
  const { zone } = useConfig();

  const [review, setReview] = useState<ReviewPayload>({
    companyNameFieldMode: 'search',
    zone,
  });
  const [page, setPage] = useState<
    Progress | ExperimentTwoProgress | undefined
  >();
  const [loading, setLoading] = useState<boolean>(false);
  const [searchCompanyProfileResult, setSearchCompanyProfileResult] =
    useState<SearchCompanyProfileResult>([]);
  const [selectedRole, setSelectedRole] = useState<ConfirmedRoles>();
  const [showCompaniesListPage, setShowCompaniesListPage] =
    useState<boolean>(false);
  const [companySelectionIsValidated, setCompanySelectionIsValidated] =
    useState<boolean>(false);
  const [isSubmitFailed, setIsSubmitFailed] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const config = useConfig();
  const { t } = useTranslations(translations);

  const { validate, getErrors } = useValidation<ReviewPayload>(
    review,
    removeUndefinedProps({
      ...validationRules(config.language),
      ...(review.isStillWorkHere
        ? {
            workHistoryTo: undefined,
            workHistory: undefined,
          }
        : {}),
    }),
  );

  const checkIfReviewIsValid = () => {
    const commonValidationRules = {
      companyName: review?.companyName,
      overallRating: review?.overallRating,
      jobTitleText: review?.jobTitleText,
      workHistoryFrom: review?.workHistoryFrom,
      workHistoryTo: review?.workHistoryTo,
      workHistory: {
        from: review?.workHistoryFrom,
        to: review?.workHistoryTo,
      },
      title: review?.title,
      isRecommended: review?.isRecommended,
      salaryRating: review?.salaryRating,
      fullLocation: review?.fullLocation,
    };

    let isValid = false;

    if (isExperimentTwo) {
      isValid = validate({
        companyName: review?.companyName,
        overallRating: review?.overallRating,
        overallDescription: review?.overallDescription,
        workLifeBalanceDescription: review?.workLifeBalanceDescription,
        perksAndBenefitsDescription: review?.perksAndBenefitsDescription,
        careerOpportunityDescription: review?.careerOpportunityDescription,
        executiveManagementDescription: review?.executiveManagementDescription,
        diversityDescription: review?.diversityDescription,
        workEnvironmentDescription: review?.workEnvironmentDescription,
      });
    } else {
      isValid = validate({
        ...commonValidationRules,
        pros: review?.theGoodThings,
        cons: review?.theChallenges,
        workType: review?.workType,
        salary: review?.salary,
        salaryType: review?.salaryType,
      });
    }

    return isValid;
  };

  const handleOnSubmit = async () => {
    setIsSubmitting(true);

    const isValid = checkIfReviewIsValid();

    if (!isValid) {
      setIsSubmitting(false);
      return;
    }
    try {
      let isSuccess = false;

      if (isExperimentTwo && page) {
        isSuccess = await submitReviewExperimentTwo(review, page);
      } else {
        isSuccess = await submitReview(review);
      }

      if (isExperimentTwo && isSuccess && page) {
        handleOnSetPage((page + 1) as ExperimentTwoProgress);
      } else if (!isExperimentTwo && isSuccess) {
        handleOnSetPage(Progress.SubmitSuccess);
      } else {
        setIsSubmitFailed(true);
      }
    } catch {
      setIsSubmitFailed(true);
    }
    setIsSubmitting(false);
  };

  const handleOnSetPage = (page: Progress | ExperimentTwoProgress) => {
    setPage(page);
    setIsSubmitFailed(false);

    scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onSearchCompanyProfiles = async (query: string) => {
    return await searchCompanyProfiles(query, SUGGESTION_LIMIT);
  };

  const onSearchLocations = async (query: string) => {
    return await searchLocations(query);
  };

  const onUpdateRole = async (role: ConfirmedRoles) => {
    if (
      role.title.text !== selectedRole?.title.text ||
      role.company?.text !== selectedRole?.company?.text ||
      role.from !== selectedRole?.from ||
      role.to !== selectedRole?.to ||
      role.id !== selectedRole?.id
    ) {
      const updatedRole = {
        ...selectedRole,
        ...role,
      };

      await updateRole(updatedRole);
      setSelectedRole(updatedRole);
    }
  };

  const handleOnCompanyNameClick = (companyIndex: number) => {
    setReview({
      ...review,
      companyName: searchCompanyProfileResult[companyIndex].data.name,
      companyId: searchCompanyProfileResult[companyIndex].companyId,
      branding: searchCompanyProfileResult[companyIndex].data.branding,
      companyProfilePublished:
        searchCompanyProfileResult[companyIndex].data.published,
      companyNameFieldMode: 'search',
    });

    setCompanySelectionIsValidated(true);
    setShowCompaniesListPage(false);

    trackClickCompanyInterceptSelection({
      config,
      context: reviewFormContext,
      selectedCompanyId: searchCompanyProfileResult[companyIndex].companyId,
      cardPosition: companyIndex,
    });
  };

  const handleOnDisambiguationBackClick = () => {
    setShowCompaniesListPage(false);

    trackClickCompanyInterceptReviewDifferentCompany({
      config,
      context: reviewFormContext,
    });
  };

  useEffect(() => {
    setPage(Progress.OverallRating);
  }, []);

  useEffect(() => {
    if (variantValueForTracking) {
      trackWriteAReviewStarted({
        config,
        context: reviewFormContext,
        variant: variantValueForTracking,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variantValueForTracking]);

  useEffect(() => {
    if (companyNameQueryParam) {
      setLoading(true);
      void searchCompanyProfiles(companyNameQueryParam).then((profiles) => {
        const isOrphanMatch =
          profiles.length === 1 &&
          profiles[0].companyId === ORPHAN_REVIEW_COMPANY_ID;
        const exactCompanyMatch = profiles.find(
          (profile) =>
            profile.data.name === companyNameQueryParam &&
            profile.companyId !== ORPHAN_REVIEW_COMPANY_ID,
        );
        const hasNonOrphanCompany = profiles.find(
          (profile) => profile.companyId !== ORPHAN_REVIEW_COMPANY_ID,
        );

        if (!profiles || profiles.length === 0 || !hasNonOrphanCompany) {
          setShowCompaniesListPage(false);
        } else if (exactCompanyMatch && !isOrphanMatch) {
          setReview({
            ...review,
            companyName: exactCompanyMatch.data.name,
            companyId: exactCompanyMatch.companyId,
            branding: exactCompanyMatch.data.branding,
            companyProfilePublished: exactCompanyMatch.data.published,
            companyNameFieldMode: 'search',
          });
          setCompanySelectionIsValidated(true);
          setShowCompaniesListPage(false);
        } else {
          setSearchCompanyProfileResult(profiles);
          setShowCompaniesListPage(true);
        }

        setLoading(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyNameQueryParam]);

  // Prevent users from accidentally leaving the page
  useEffect(() => {
    if (!isExperimentTwo && page === Progress.SubmitSuccess) {
      return;
    }
    if (isExperimentTwo && page === ExperimentTwoProgress.SubmitSuccess) {
      return;
    }

    function beforeUnload(e: BeforeUnloadEvent) {
      e.preventDefault();
    }

    window.addEventListener('beforeunload', beforeUnload);

    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
    };
  }, [isExperimentTwo, page]);

  useEffect(() => {
    if (!review.companyName) {
      return;
    }
    void getConfirmedRoles().then((confirmedRoles) => {
      const currentRole = confirmedRoles.find(
        (role) =>
          role.company?.text.toLowerCase() ===
          review.companyName?.toLowerCase(),
      );
      if (currentRole) {
        setSelectedRole(currentRole);
        setReview({
          ...review,
          workHistoryFrom: currentRole.from,
          ...(currentRole.to
            ? { workHistoryTo: currentRole.to, isStillWorkHere: false }
            : { isStillWorkHere: true }),
          workHistory: {
            from: currentRole.from,
            to: currentRole.to,
          },
          jobTitleText: currentRole.title.text,
          roleId: currentRole.id,
          isWorkHistoryPrefilled: true,
        });
      } else {
        setReview({
          ...review,
          workHistoryFrom: undefined,
          workHistoryTo: undefined,
          workHistory: undefined,
          isStillWorkHere: false,
          jobTitleText: '',
          roleId: uuid(),
          isWorkHistoryPrefilled: false,
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [review.companyName]);

  // For Experiment Two only
  useEffect(() => {
    if (experimentTwoReviewId) {
      setReview((review) => ({
        ...review,
        reviewId: experimentTwoReviewId,
      }));
    }
  }, [experimentTwoReviewId]);

  if (loading) {
    return <Loader />;
  }

  if (showCompaniesListPage) {
    return (
      <CompaniesListPage
        searchCompanyProfileResult={searchCompanyProfileResult}
        handleOnCompanyNameClick={handleOnCompanyNameClick}
        handleOnDisambiguationBackClick={handleOnDisambiguationBackClick}
        reviewFormContext={reviewFormContext}
      />
    );
  }

  if (isExperimentTwo) {
    return (
      <>
        {page === ExperimentTwoProgress.OverallRating && (
          <ReviewCompanyPage
            review={review}
            reviewFormContext={reviewFormContext}
            searchCompanyProfiles={onSearchCompanyProfiles}
            hideCompanyNameSearch={companySelectionIsValidated}
            setPage={handleOnSetPage}
            setter={setReview}
            onSubmit={() => {
              void handleOnSubmit();
            }}
            isSubmitting={isSubmitting}
            pageNumber={page}
          />
        )}
        {page === ExperimentTwoProgress.WorkLifeBalance && (
          <WorkLifeBalancePage
            review={review}
            reviewFormContext={reviewFormContext}
            setPage={handleOnSetPage}
            setter={setReview}
            onSubmit={() => {
              void handleOnSubmit();
            }}
            isSubmitting={isSubmitting}
            pageNumber={page}
          />
        )}
        {page === ExperimentTwoProgress.PerksAndBenefits && (
          <PerksAndBenefitsPage
            review={review}
            reviewFormContext={reviewFormContext}
            setPage={handleOnSetPage}
            setter={setReview}
            onSubmit={() => {
              void handleOnSubmit();
            }}
            isSubmitting={isSubmitting}
            pageNumber={page}
          />
        )}
        {page === ExperimentTwoProgress.Salary && (
          <SalaryPage
            review={review}
            reviewFormContext={reviewFormContext}
            setPage={handleOnSetPage}
            setter={setReview}
            onSubmit={() => {
              void handleOnSubmit();
            }}
            isSubmitting={isSubmitting}
            pageNumber={page}
          />
        )}
        {page === ExperimentTwoProgress.CareerDevelopment && (
          <CareerDevelopmentPage
            review={review}
            reviewFormContext={reviewFormContext}
            setPage={handleOnSetPage}
            setter={setReview}
            onSubmit={() => {
              void handleOnSubmit();
            }}
            isSubmitting={isSubmitting}
            pageNumber={page}
          />
        )}
        {page === ExperimentTwoProgress.Management && (
          <ManagementPage
            review={review}
            reviewFormContext={reviewFormContext}
            setPage={handleOnSetPage}
            setter={setReview}
            onSubmit={() => {
              void handleOnSubmit();
            }}
            isSubmitting={isSubmitting}
            pageNumber={page}
          />
        )}
        {page === ExperimentTwoProgress.DiversityAndEqualOpportunity && (
          <DiversityEqualOpptyPage
            review={review}
            reviewFormContext={reviewFormContext}
            setPage={handleOnSetPage}
            setter={setReview}
            onSubmit={() => {
              void handleOnSubmit();
            }}
            isSubmitting={isSubmitting}
            pageNumber={page}
          />
        )}
        {page === ExperimentTwoProgress.WorkingEnvironment && (
          <WorkingEnvironmentPage
            review={review}
            reviewFormContext={reviewFormContext}
            setPage={handleOnSetPage}
            setter={setReview}
            onSubmit={() => {
              void handleOnSubmit();
            }}
            isSubmitting={isSubmitting}
            pageNumber={page}
          />
        )}
        {page === ExperimentTwoProgress.SubmitSuccess && (
          <ExpTwoSubmitSuccessPage
            companyName={review.companyName ?? ''}
            reviewFormContext={reviewFormContext}
          />
        )}
      </>
    );
  }

  return (
    <Box data={{ testid: 'write-a-review-page' }}>
      <MetaTag
        title={`${t('Write a review')} | ${brandName[config.brand]}`}
        description={t(
          'Your review will help someone else and it only takes a few minutes. All reviews are anonymous. We wont share your name, only details about your role.',
        )}
      />
      <NoIndexTag />
      {page === Progress.OverallRating && (
        <OverallRatingPage
          review={review}
          reviewFormContext={reviewFormContext}
          searchCompanyProfiles={onSearchCompanyProfiles}
          setPage={handleOnSetPage}
          setter={setReview}
          pageNumber={page}
          hideCompanyNameSearch={companySelectionIsValidated}
        />
      )}
      {page === Progress.YourRole && (
        <YourRolePage
          review={review}
          reviewFormContext={reviewFormContext}
          searchLocations={onSearchLocations}
          setPage={handleOnSetPage}
          setter={setReview}
          getRoleTitles={getRoleTitles}
          pageNumber={page}
          onUpdateRole={onUpdateRole}
        />
      )}
      {page === Progress.YourExperience && (
        <YourExperiencePage
          review={review}
          reviewFormContext={reviewFormContext}
          setPage={handleOnSetPage}
          setter={setReview}
          pageNumber={page}
        />
      )}
      {page === Progress.YourSalary && (
        <YourSalaryPage
          review={review}
          reviewFormContext={reviewFormContext}
          setPage={handleOnSetPage}
          setter={setReview}
          pageNumber={page}
        />
      )}
      {page === Progress.YourFeedback && (
        <YourFeedbackPage
          review={review}
          reviewFormContext={reviewFormContext}
          setPage={handleOnSetPage}
          setter={setReview}
          onSubmit={() => {
            void handleOnSubmit();
          }}
          pageNumber={page}
          formErrors={getErrors()}
          isSubmitFailed={isSubmitFailed}
          isSubmitting={isSubmitting}
          onAlertClosed={() => setIsSubmitFailed(false)}
        />
      )}
      {page === Progress.SubmitSuccess && (
        <SubmitSuccessPage
          companyName={review.companyName ?? ''}
          reviewFormContext={reviewFormContext}
          companyId={
            review.companyId === ORPHAN_REVIEW_COMPANY_ID
              ? undefined
              : review.companyId
          }
          published={review.companyProfilePublished}
        />
      )}
    </Box>
  );
};
